import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SchoolCoursesService } from '@digilize/language-school/data';
import { SchoolLessonType } from '@digilize/language-school/definitions';
import { ShopQuizzesApiService } from '@digilize/shared/data/services/src';
import { Media, QuizQuestionType, QuizSessionAnswerRequest, QuizSessionState } from '@digilize/shared/definitions/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { combineLatest, first, map, switchMap, tap, throwError } from 'rxjs';

import { ExercisesService } from '../exercises.service';
import { TextDialogComponent, TextDialogComponentData } from './text-dialog/text-dialog.component';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';

@Component({
  selector: 'lsl-exercises',
  templateUrl: './exercises.component.html',
  styleUrls: ['./exercises.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExercisesComponent implements OnInit, OnDestroy {
  QuizQuestionType = QuizQuestionType;
  progressProcent = 100;
  expired: boolean;
  readingText: string;
  reset = false;
  readingAudio: Media;
  orderAnswerElements: number;
  wasFullScreenReadingClosed = false;
  question$ = combineLatest([this.exercisesService.quiz$, this.exercisesService.exercise$]).pipe(
    tap(([quiz, exercise]) => {
      this.orderAnswerElements = quiz.question?.answers?.length || 0;
      this.handleQuestionChange(quiz);
    }),
    map(([quiz, exercise]) => {
      return { quiz, exercise };
    })
  );
  time = '00:00';
  endOfTime = false;
  withTimer = false;
  timeInterval;
  form = new FormControl();
  exerciseId: string = null;
  lessonId: string = null;
  SchoolLessonType;
  skipped = false;

  constructor(
    private schoolCoursesService: SchoolCoursesService,
    private route: ActivatedRoute,
    private exercisesService: ExercisesService,
    private shopQuizzesApiService: ShopQuizzesApiService,
    private dialogService: DialogService,
    private router: Router,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          if (params['exerciseId'] && params['lessonId']) {
            this.exerciseId = params['exerciseId'];
            this.lessonId = params['lessonId'];
            return combineLatest([
              this.schoolCoursesService.getExercise(this.exerciseId),
              this.shopQuizzesApiService.getPublicShopQuiz(this.exerciseId),
            ]);
          }
          return throwError(() => 'not exercise Id');
        }),
        first()
      )
      .subscribe({
        next: ([exercise, shopQuiz]) => {
          const { description_long, audio } = shopQuiz.quiz;
          this.readingText = description_long;
          this.readingAudio = audio;
          if (!this.exercisesService.quiz) {
            this.exercisesService.play(exercise, this.lessonId);
          }
        },
        error: (error) => {
          this.snackbarService.openSnackbarWithApiError(error.error.error);
          throw new Error(error);
        },
      });
  }

  handleQuestionChange(quiz: QuizSessionState) {
    this.wasFullScreenReadingClosed = JSON.parse(
      sessionStorage.getItem(`wasFullScreenReadingClosed-${quiz.shop_quiz_session_id}`)
    );
    if (quiz.answer) {
      this.form.disable();
    }
    const withAnswer = quiz.answer;
    if (
      quiz.question?.max_seconds &&
      this.exercisesService.exercise &&
      !this.exercisesService.exercise?.not_timed &&
      !withAnswer
    ) {
      this.initTimer(quiz);
    }
  }
  initTimer(quiz: QuizSessionState) {
    clearInterval(this.timeInterval);
    this.withTimer = true;
    const sessionQuestionId = `${quiz.shop_quiz_session_id}-${quiz.question.id}`;
    let startingDate: Date;
    if (sessionStorage.getItem(sessionQuestionId)) {
      startingDate = new Date(Number(sessionStorage.getItem(sessionQuestionId)));
    } else {
      startingDate = new Date();
      sessionStorage.setItem(sessionQuestionId, JSON.stringify(new Date().getTime()));
    }
    const ms = this.getMsPastTime(startingDate) + quiz.question.max_seconds * 1000;
    this.time = this.getTime(Math.trunc(ms / 1000));
    if (ms > 0) {
      this.startCounting(startingDate, quiz);
    } else {
      this.endOfTime = true;
      clearInterval(this.timeInterval);
      if (!this.skipped) {
        this.skipExercise(quiz);
      }
    }
  }
  restartExercise(lessonWasCompletedEarlier: boolean) {
    this.schoolCoursesService
      .getLesson(this.lessonId)
      .pipe(first())
      .subscribe((lesson) => {
        if (!lessonWasCompletedEarlier && lesson.is_completed) {
          this.schoolCoursesService.shouldAnimateLastFilledRoute = true;
        }
      });

    this.form = new FormControl();
    this.schoolCoursesService
      .getExercise(this.exerciseId)
      .pipe(first())
      .subscribe((exercise) => {
        this.exercisesService.play(exercise, this.lessonId, true);
      });
  }

  handleContinue(lessonWasCompletedEarlier: boolean, exercisePassed: boolean) {
    if (!exercisePassed) {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }
    combineLatest([this.exercisesService.exercise$, this.schoolCoursesService.getLesson(this.lessonId)])
      .pipe(first())
      .subscribe(([exercise, lesson]) => {
        const isExam = !exercise.not_timed;

        if (lesson.is_completed && isExam) {
          if (!lessonWasCompletedEarlier) {
            this.schoolCoursesService.shouldAnimateLastFilledRoute = true;
          }
          if (lesson.type === SchoolLessonType.Exam) {
            /* we assume lesson of type exam is only at the end of course */
            this.router.navigate(['course', this.schoolCoursesService.currentCourseId, 'finished']);
          } else {
            this.router.navigate(['../', 'finished'], { relativeTo: this.route });
          }
        } else {
          this.router.navigate(['../'], { relativeTo: this.route });
        }
      });
  }

  nextQuestion(quiz: QuizSessionState) {
    this.skipped = false;
    this.reset = true;
    sessionStorage.removeItem(`${quiz.shop_quiz_session_id}-${quiz.question.id}`);
    this.form.enable();
    clearInterval(this.timeInterval);
    this.form = new FormControl();

    this.schoolCoursesService
      .getExercise(this.exerciseId)
      .pipe(first())
      .subscribe((exercise) => {
        this.exercisesService.play(exercise, this.lessonId);
        this.endOfTime = false;
      });
  }

  startCounting(start, quizSessionState: QuizSessionState) {
    this.timeInterval = setInterval(() => {
      const msPast = this.getMsPastTime(start) + quizSessionState.question.max_seconds * 1000;
      this.expired = msPast < 0;
      if (this.expired) {
        this.endOfTime = true;
        this.skipExercise(quizSessionState);
      }

      this.time = this.getTime(Math.trunc(msPast / 1000));
    }, 100);
  }

  handleExerciseSubmit(quizSessionState: QuizSessionState) {
    const value = this.form.getRawValue();
    if (!value) return;
    sessionStorage.removeItem(`${quizSessionState.shop_quiz_session_id}-${quizSessionState.question.id}`);
    let answer: QuizSessionAnswerRequest = { shop_quiz_id: quizSessionState.shop_quiz_id };
    switch (quizSessionState.question.type) {
      case QuizQuestionType.Single:
        answer.answer_single = { quiz_question_answer_id: value };
        break;
      case QuizQuestionType.Text:
        answer.answer_text = { quiz_question_id: quizSessionState.question.id, answer: value };
        break;
      case QuizQuestionType.Order:
        if (this.orderAnswerElements !== (value as Array<string>).length) return;
        answer.answer_order = { quiz_question_id: quizSessionState.question.id, answer: value };
        break;
    }

    clearInterval(this.timeInterval);
    this.exercisesService.submitAnswer(this.exerciseId, this.lessonId, answer);
  }

  skipExercise(quizSessionState: QuizSessionState) {
    this.skipped = true;
    this.form.disable();
    let answer: QuizSessionAnswerRequest = {
      shop_quiz_id: quizSessionState.shop_quiz_id,
      quiz_question_skip_id: quizSessionState.question.id,
    };
    clearInterval(this.timeInterval);
    this.exercisesService.submitAnswer(this.exerciseId, this.lessonId, answer);
  }

  getMsPastTime(pastTime: Date): number {
    return new Date(pastTime).getTime() - new Date().getTime();
  }

  getTime(seconds: number): string {
    const minutesPart = seconds < 0 ? 0 : Math.floor(seconds / 60);
    const secondsPart = seconds < 0 ? 0 : seconds % 60;
    return `${String(minutesPart).padStart(2, '0')} : ${String(secondsPart).padStart(2, '0')} `;
  }

  handleOpenText(readingText) {
    const data: TextDialogComponentData = { html: readingText, audio: this.readingAudio };
    this.dialogService.openDialog(TextDialogComponent, { width: '700px', data });
  }

  handleGoToQuestionsClick(quizSessionId: string) {
    this.wasFullScreenReadingClosed = true;
    sessionStorage.setItem(
      `wasFullScreenReadingClosed-${quizSessionId}`,
      JSON.stringify(this.wasFullScreenReadingClosed)
    );
  }

  ngOnDestroy(): void {
    clearInterval(this.timeInterval);
  }
}
